import type { PreviewFile } from './types'

const IMAGE_EXTENSIONS = /\.(jpe?g|png|gif|bmp|webp|svg|ico|tiff?)(?:[#?]|$)/i

/** 长按达到此时长后，松手 click 不再关闭预览器 */
export const WECHAT_IMAGE_LONG_PRESS_MS = 480
/** 长按过程中允许的最大位移，超出则视为滑动 */
export const WECHAT_IMAGE_LONG_PRESS_MOVE_TOLERANCE = 8

export type WechatImageLongPressEnv = {
    isMobile: boolean
    isWechatBrowser: boolean
    isMiniprogram: boolean
}

export function isImageFile(file: Pick<PreviewFile, 'type' | 'url'>): boolean {
    return file.type === 'image' || IMAGE_EXTENSIONS.test(file.url)
}

/**
 * 从文件列表抽出微信 previewImage 需要的 urls。
 * 多图会全部带上，current 对应当前点开的那张，微信原生预览可左右切换。
 */
export function getWechatPreviewImages(fileList: PreviewFile[], currentIndex = 0): { current: string; urls: string[] } | null {
    const images = fileList.filter(isImageFile)
    if (images.length === 0) {
        return null
    }

    const urls = images.map(file => file.url)
    const currentFile = fileList[currentIndex]
    const current = currentFile && isImageFile(currentFile) ? currentFile.url : urls[0]
    return { current, urls }
}

export function isWechatMobileEnv(env: WechatImageLongPressEnv): boolean {
    return env.isMobile && (env.isWechatBrowser || env.isMiniprogram)
}

/**
 * 微信浏览器 / 小程序 web-view 中，自定义预览器用原生 img 长按弹出「识别图片 / 保存图片」。
 * 点击预览已走 wx.previewImage；这里只在 FilePreviewer 实际渲染时生效（SDK 失败回退、混入非图片等）。
 */
export function shouldEnableWechatNativeImageMenu(
    file: Pick<PreviewFile, 'type' | 'url'> | undefined,
    env: WechatImageLongPressEnv
): boolean {
    return Boolean(isWechatMobileEnv(env) && file && isImageFile(file))
}

export type WechatImageLongPressMode = 'native-menu'

/**
 * 微信浏览器 / 小程序 web-view 中，图片长按统一走原生菜单。
 * 不满足条件时返回 null。
 */
export function getWechatImageLongPressMode(
    file: Pick<PreviewFile, 'type' | 'url'> | undefined,
    env: WechatImageLongPressEnv
): WechatImageLongPressMode | null {
    return shouldEnableWechatNativeImageMenu(file, env) ? 'native-menu' : null
}

export function shouldTriggerWechatImageLongPress({
    durationMs,
    moveDistance,
    delayMs = WECHAT_IMAGE_LONG_PRESS_MS,
    moveTolerance = WECHAT_IMAGE_LONG_PRESS_MOVE_TOLERANCE
}: {
    durationMs: number
    moveDistance: number
    delayMs?: number
    moveTolerance?: number
}): boolean {
    return durationMs >= delayMs && moveDistance <= moveTolerance
}
